import { computeKeywordIdf, weightedKeywordJaccard } from '@/viz/graph';
import { getStoredUserPapers, addStoredUserPaper, removeStoredUserPaper } from './userPapers';
import { assignPaperIds } from './paperIds';

const BASE_PATH = import.meta.env.BASE_URL || '/';
const PAPERS_FILE = 'research_artifacts.json';

let cachedPapers = null;
let cachedConfig = null;
let cachedKeywordLinks = null;

export async function loadPapers() {
  if (cachedPapers) return cachedPapers;

  const res = await fetch(`${BASE_PATH}data/${PAPERS_FILE}`);
  if (!res.ok) throw new Error(`Failed to load ${PAPERS_FILE}: ${res.status}`);
  const records = await res.json();
  const allRecords = [...records, ...getStoredUserPapers()];

  cachedPapers = assignPaperIds(allRecords);
  return cachedPapers;
}

/** Precomputed keyword-similarity scores for pairs involving anonymized
 * papers, so the graph can draw Shared Keywords edges for them without the
 * client ever having their real keyword text (see
 * scripts/build-keyword-links.mjs). Missing file just means no such edges. */
export async function loadKeywordLinks() {
  if (cachedKeywordLinks) return cachedKeywordLinks;
  const linksFile = PAPERS_FILE.replace(/\.json$/, '.keyword-links.json');
  const res = await fetch(`${BASE_PATH}data/${linksFile}`);
  cachedKeywordLinks = res.ok ? await res.json() : [];
  return cachedKeywordLinks;
}

export async function addUserPaper(record) {
  addStoredUserPaper(record);
  cachedPapers = null;
  const papers = await loadPapers();
  return papers.find((p) => p._userKey === record._userKey) || null;
}

export async function removeUserPaper(userKey) {
  removeStoredUserPaper(userKey);
  cachedPapers = null;
  return loadPapers();
}

export async function loadConfig() {
  if (cachedConfig) return cachedConfig;
  const res = await fetch(`${BASE_PATH}data/site.config.json`);
  cachedConfig = res.ok ? await res.json() : {};
  return cachedConfig;
}

export function getPaperById(papers, id) {
  return papers.find((p) => p._id === id) || null;
}

export function getRelatedPapers(papers, paper, limit = 6) {
  const keywords = paper.contribution?.keywords || [];
  if (keywords.length === 0) return [];

  const idf = computeKeywordIdf(papers.map((p) => ({ keywords: p.contribution?.keywords || [] })));

  return papers
    .filter((p) => p._id !== paper._id)
    .map((p) => ({
      paper: p,
      score: weightedKeywordJaccard(keywords, p.contribution?.keywords || [], idf),
    }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(({ paper: p }) => ({ ...p, relationship: 'shared-keywords' }));
}
